import { useState } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar, Mail, Phone, User } from 'lucide-react';
import { format } from 'date-fns';

interface Contact {
  id: string;
  first_name?: string;
  last_name?: string;
  primary_phone?: string;
  primary_email?: string;
  created_at?: string;
  // assigned_sales_consultant_id removido
}

interface DuplicateGroup {
  phone: string;
  contacts: Contact[];
}

interface MergeDuplicatesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  duplicates: DuplicateGroup[];
  onMerge: (keepId: string, mergeIds: string[]) => Promise<void>;
  loading: boolean;
}

export function MergeDuplicatesDialog({
  open,
  onOpenChange,
  duplicates,
  onMerge,
  loading
}: MergeDuplicatesDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedKeepId, setSelectedKeepId] = useState<string>('');

  const currentGroup = duplicates[currentIndex];
  const hasMore = currentIndex < duplicates.length - 1;

  const handleMerge = async () => {
    if (!selectedKeepId || !currentGroup) return;

    const mergeIds = currentGroup.contacts
      .filter(c => c.id !== selectedKeepId)
      .map(c => c.id);

    await onMerge(selectedKeepId, mergeIds);

    // Ir para próximo grupo ou fechar
    if (hasMore) {
      setCurrentIndex(prev => prev + 1);
      setSelectedKeepId('');
    } else {
      onOpenChange(false);
      setCurrentIndex(0);
      setSelectedKeepId('');
    }
  };

  const handleSkip = () => {
    if (hasMore) {
      setCurrentIndex(prev => prev + 1);
      setSelectedKeepId('');
    } else {
      onOpenChange(false);
      setCurrentIndex(0);
      setSelectedKeepId('');
    }
  };

  if (!currentGroup) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mesclar Contatos Duplicados</DialogTitle>
          <DialogDescription>
            Encontrados {duplicates.length} grupo(s) de duplicatas. 
            Grupo {currentIndex + 1} de {duplicates.length}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>Telefone: {currentGroup.phone}</span>
          </div>

          <RadioGroup value={selectedKeepId} onValueChange={setSelectedKeepId}>
            <div className="space-y-3">
              {currentGroup.contacts.map((contact) => (
                <Card 
                  key={contact.id} 
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedKeepId === contact.id ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => setSelectedKeepId(contact.id)}
                >
                  <div className="flex items-start gap-3">
                    <RadioGroupItem value={contact.id} id={contact.id} />
                    <Label htmlFor={contact.id} className="flex-1 cursor-pointer">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">
                            {contact.first_name || ''} {contact.last_name || ''}
                          </span>
                          {selectedKeepId === contact.id && (
                            <Badge variant="default">Manter</Badge>
                          )}
                        </div>

                        <div className="space-y-1 text-sm text-muted-foreground">
                          {contact.primary_email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3 w-3" />
                              <span>{contact.primary_email}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            <span>{contact.primary_phone}</span>
                          </div>

                          {contact.created_at && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3" />
                              <span>
                                Criado em: {safeFormat(new Date(contact.created_at), 'dd/MM/yyyy HH:mm')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Label>
                  </div>
                </Card>
              ))}
            </div>
          </RadioGroup>

          <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            <strong>Atenção:</strong> O contato selecionado será mantido e todos os outros registros 
            (ligações, deals, mensagens, etc.) dos contatos duplicados serão transferidos para ele. 
            Os contatos duplicados serão excluídos permanentemente.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={loading}
          >
            {hasMore ? 'Pular' : 'Fechar'}
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!selectedKeepId || loading}
          >
            {loading ? 'Mesclando...' : 'Mesclar Contatos'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
